import { useState } from 'react';
import type { FormEvent } from 'react';
import {
  fetchSession,
  requestLoginCode as apiRequestLoginCode,
  verifyLoginCode as apiVerifyLoginCode,
} from '../../api/auth.ts';
import { sessionUserFromResponse, useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, FormField, Input } from '../../ui/index.ts';
import { localizeAuthError } from './authErrors.ts';
import type { AuthErrorKeys } from './authErrors.ts';

/**
 * Email sign-in-code flow (epic #1475) — the second tab of the login page.
 *
 * This is the ONLY way into an invite-activated account: activation leaves
 * `password_hash` NULL, so those customers have no password to type. It is
 * open to password accounts too, as an alternative.
 *
 * Two things the UI must not do, both of them anti-enumeration:
 *
 *   - The request step's acknowledgement is conditional copy ("if that
 *     address has an account…"). The endpoint answers 202 identically for
 *     registered, unknown, suspended, and deleted addresses — and does
 *     compensating work so even the latency matches. Copy that said "code
 *     sent" would re-open by assertion the oracle the backend closed.
 *
 *   - Account status is surfaced only after a correct code is submitted.
 *     That is the server's split, and it holds because the caller has by
 *     then proven they hold the mailbox.
 *
 * There is no client-side resend countdown. The resend interval is a
 * server-side runtime setting that this response does not carry, and
 * inventing a local timer would either contradict the real limit or need a
 * second source of truth for it. The button stays live; `RATE_LIMITED`
 * comes back inline when it is too soon.
 */

const LOGIN_CODE_PATTERN = /^[0-9]{6}$/;

const REQUEST_ERROR_KEYS: AuthErrorKeys = {
  RATE_LIMITED: 'login.code.error.send_rate_limited',
};

const VERIFY_ERROR_KEYS: AuthErrorKeys = {
  CODE_INVALID: 'login.code.error.invalid',
  CODE_EXPIRED: 'login.code.error.expired',
  CODE_RATE_LIMITED: 'login.code.error.attempts_exhausted',
  RATE_LIMITED: 'login.code.error.verify_rate_limited',
  ACCOUNT_SUSPENDED: 'login.code.error.account_suspended',
  ACCOUNT_DELETED: 'login.code.error.account_deleted',
};

type Step =
  | { readonly kind: 'request' }
  | { readonly kind: 'verify'; readonly email: string; readonly expiresInSeconds: number };

interface LoginCodeFormProps {
  /** Called once the session context holds the new principal. */
  readonly onSignedIn: () => void;
}

export function LoginCodeForm({ onSignedIn }: LoginCodeFormProps): JSX.Element {
  const { setUser, fetchImpl } = useSession();
  const t = useT();
  const [step, setStep] = useState<Step>({ kind: 'request' });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);

  async function sendCode(target: string): Promise<void> {
    const ack = await apiRequestLoginCode({ email: target }, fetchImpl);
    setStep({ kind: 'verify', email: target, expiresInSeconds: ack.expiresInSeconds });
  }

  async function handleRequest(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (trimmed.length === 0) {
      setError(t('login.code.error.empty_email'));
      return;
    }
    setSubmitting(true);
    try {
      await sendCode(trimmed);
      setCode('');
    } catch (err) {
      setError(localizeAuthError(err, t, REQUEST_ERROR_KEYS));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend(target: string): Promise<void> {
    setError(null);
    setResent(false);
    setSubmitting(true);
    try {
      await sendCode(target);
      setResent(true);
    } catch (err) {
      setError(localizeAuthError(err, t, REQUEST_ERROR_KEYS));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerify(e: FormEvent<HTMLFormElement>, target: string): Promise<void> {
    e.preventDefault();
    setError(null);
    setResent(false);
    const trimmed = code.trim();
    // Checked here, not just server-side: a malformed code is refused before
    // the attempt counter but still spends one of the caller's rate-limit
    // slots, so a typo'd length would cost them a real retry.
    if (!LOGIN_CODE_PATTERN.test(trimmed)) {
      setError(t('login.code.error.format'));
      return;
    }
    setSubmitting(true);
    try {
      await apiVerifyLoginCode({ email: target, code: trimmed }, fetchImpl);
      const session = await fetchSession(fetchImpl);
      setUser(sessionUserFromResponse(session));
      onSignedIn();
    } catch (err) {
      setError(localizeAuthError(err, t, VERIFY_ERROR_KEYS));
    } finally {
      setSubmitting(false);
    }
  }

  if (step.kind === 'request') {
    return (
      <form className="flex flex-col gap-4" onSubmit={handleRequest} noValidate>
        <FormField label={t('login.email')} fieldId="login-code-email">
          <Input
            id="login-code-email"
            type="email"
            mono
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            disabled={submitting}
            required
            data-testid="login-code-email"
          />
        </FormField>
        {error !== null ? (
          <p className="m-0 text-label text-bad" role="alert" data-testid="login-code-error">
            {error}
          </p>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          loading={submitting}
          data-testid="login-code-send"
          className="mt-1 h-9 w-full"
        >
          {submitting ? t('login.code.sending') : t('login.code.send')}
        </Button>
      </form>
    );
  }

  const stepEmail = step.email;
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        void handleVerify(e, stepEmail);
      }}
      noValidate
    >
      <p className="m-0 text-caption text-ink-low" role="status" data-testid="login-code-ack">
        {t(resent ? 'login.code.ack_resent' : 'login.code.ack', {
          email: stepEmail,
          minutes: Math.max(1, Math.round(step.expiresInSeconds / 60)),
        })}
      </p>
      <FormField label={t('login.code.label')} fieldId="login-code-value">
        <Input
          id="login-code-value"
          mono
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.currentTarget.value)}
          disabled={submitting}
          required
          data-testid="login-code-value"
        />
      </FormField>
      {error !== null ? (
        <p className="m-0 text-label text-bad" role="alert" data-testid="login-code-error">
          {error}
        </p>
      ) : null}
      <Button
        type="submit"
        variant="primary"
        loading={submitting}
        data-testid="login-code-submit"
        className="mt-1 h-9 w-full"
      >
        {submitting ? t('login.submitting') : t('login.submit')}
      </Button>
      <div className="flex items-center justify-between gap-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={submitting}
          data-testid="login-code-resend"
          onClick={() => {
            void handleResend(stepEmail);
          }}
        >
          {t('login.code.resend')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={submitting}
          data-testid="login-code-change-email"
          onClick={() => {
            setStep({ kind: 'request' });
            setError(null);
            setResent(false);
            setCode('');
          }}
        >
          {t('login.code.change_email')}
        </Button>
      </div>
    </form>
  );
}
